import React from 'react';

import {
  isUnrenderable,
  isValidElementOfType,
  ZINDEX_GROUP_INPUT_FOCUS,
} from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { css } from '@tanium/style-system';

import { CONTROL_GROUP_ITEM_COREUITYPE, ControlGroupItemProps } from './ControlGroupItem';

const ItemWrapper = styled('div')<ControlGroupItemProps>(({ grow }) =>
  css((theme) => ({
    borderRadius: 0,
    display: 'flex',
    flexGrow: grow,
    marginRight: `-${theme.borderWidths[1]}px`,
    '&& > *': {
      flex: 'inherit',
      borderRadius: 'inherit',
    },
    'input:focus': {
      zIndex: ZINDEX_GROUP_INPUT_FOCUS,
    },
  })),
);

const ControlGroupContainer = styled('div')(
  css({
    display: 'flex',
    [`> ${ItemWrapper}:first-child`]: {
      borderTopLeftRadius: 1,
      borderBottomLeftRadius: 1,
    },
    [`> ${ItemWrapper}:last-child`]: {
      borderTopRightRadius: 1,
      borderBottomRightRadius: 1,
      marginRight: 0,
    },
  }),
);

const isControlGroupItemElement = (
  child: React.ReactNode,
): child is React.ReactElement<ControlGroupItemProps> =>
  isValidElementOfType(child, CONTROL_GROUP_ITEM_COREUITYPE);

const extractControlGroupItemProps = (child: React.ReactNode) => {
  if (!isControlGroupItemElement(child)) {
    return {} as Partial<ControlGroupItemProps>;
  }
  return child.props;
};

export interface Props {
  /**
   * Inputs and Buttons to be rendered as one unit
   */
  children?: React.ReactNode;
  /**
   * The `flex-grow` value to be applied to all Control Group Items. Defaults to `0`.
   */
  itemGrow?: number;
}

const ControlGroup = ({ children, itemGrow = 0 }: Props) => (
  <ControlGroupContainer>
    {React.Children.map(children, (child) => {
      const { children: innerChildren = child, grow = itemGrow } = extractControlGroupItemProps(
        child,
      );

      if (isUnrenderable(innerChildren)) {
        return null;
      }

      return <ItemWrapper grow={grow}>{innerChildren}</ItemWrapper>;
    })}
  </ControlGroupContainer>
);

ControlGroup.displayName = 'ControlGroup';

export default ControlGroup;
